import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Printer, FileText, ChevronDown, FileSpreadsheet, FileDown } from "lucide-react";
import api, { apiErrorMsg } from "@/lib/api";
import { downloadFile } from "@/lib/download";
import PrintSosio from "@/components/PrintSosio";

export default function HasilSosioPage() {
  const [periodes, setPeriodes] = useState([]);
  const [kelompokList, setKelompokList] = useState([]);
  const [kelompokFilter, setKelompokFilter] = useState("");
  const [periodeId, setPeriodeId] = useState("");
  const [data, setData] = useState(null);
  const [siswaIdx, setSiswaIdx] = useState(0);

  useEffect(() => {
    api.get("/admin/periode").then((r) => setPeriodes(r.data));
    api.get("/admin/kelompok").then((r) => setKelompokList(r.data));
  }, []);

  const filteredPeriodes = kelompokFilter ? periodes.filter((p) => p.kelompok_id === kelompokFilter) : periodes;

  useEffect(() => {
    if (!periodeId) { setData(null); return; }
    api.get(`/admin/periode/${periodeId}/hasil`).then((r) => { setData(r.data); setSiswaIdx(0); }).catch((e) => toast.error(apiErrorMsg(e)));
  }, [periodeId]);

  const printAll = () => {
    if (!data) return;
    document.body.classList.add("printing-all");
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.classList.remove("printing-all"), 500);
    }, 100);
  };

  const printSingle = () => window.print();

  const current = data?.hasil?.[siswaIdx];

  return (
    <div className="p-4 md:p-8">
      <div className="no-print">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-brass-600 font-semibold mb-1">Hasil</div>
          <h1 className="font-heading text-3xl font-bold text-navy-900">Hasil Sosiometri</h1>
          <p className="text-slate-500 mt-1 text-sm">Dokumen NILAI SIKAP DAN PERILAKU PERORANGAN per Serdik.</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="hasil-sosio-filter">
          <div className="w-full sm:w-auto">
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Kelompok</label>
            <select value={kelompokFilter} onChange={(e) => { setKelompokFilter(e.target.value); setPeriodeId(""); }} data-testid="hasil-sosio-kelompok-filter" className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white w-full sm:w-auto sm:min-w-[220px]">
              <option value="">-- Pilih kelompok dulu --</option>
              {kelompokList.map((k) => <option key={k.id} value={k.id}>{k.nama}{k.pendidikan ? ` — ${k.pendidikan}` : ""}</option>)}
            </select>
          </div>
          <div className="w-full sm:w-auto">
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Periode</label>
            <select value={periodeId} onChange={(e) => setPeriodeId(e.target.value)} disabled={!kelompokFilter} data-testid="hasil-sosio-periode-select" className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white w-full sm:w-auto sm:min-w-[260px] disabled:bg-slate-100 disabled:text-slate-400">
              <option value="">{kelompokFilter ? (filteredPeriodes.length === 0 ? "(belum ada periode untuk kelompok ini)" : "-- Pilih periode --") : "-- Pilih kelompok dulu --"}</option>
              {filteredPeriodes.map((p) => <option key={p.id} value={p.id}>{p.jenis === "mingguan" ? "Minggu" : "Bulan"} ke-{p.nomor} • {p.bulan} {p.tahun} • {p.pengasuh_nama}</option>)}
            </select>
          </div>
          {data && (
            <div className="w-full sm:w-auto">
              <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Serdik</label>
              <select value={siswaIdx} onChange={(e) => setSiswaIdx(Number(e.target.value))} data-testid="hasil-sosio-siswa-select" className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white w-full sm:w-auto sm:min-w-[260px]">
                {data.hasil.map((h, i) => <option key={h.siswa.id} value={i}>{i + 1}. {h.siswa.nama}</option>)}
              </select>
            </div>
          )}
          {data && (
            <div className="flex flex-wrap gap-2">
              <button onClick={printSingle} data-testid="print-sosio-btn" className="bg-navy-900 hover:bg-navy-800 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2">
                <Printer className="w-4 h-4" /> Cetak Halaman Ini
              </button>
              <button onClick={printAll} className="bg-brass-500 hover:bg-brass-600 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2">
                <FileText className="w-4 h-4" /> Cetak Semua
              </button>
              <button onClick={() => downloadFile(`/admin/periode/${periodeId}/export-pdf/sosiometri?siswa_id=${data.hasil[siswaIdx].siswa.id}`, "Sosiometri.pdf")} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2" data-testid="pdf-sosio-single"><FileDown className="w-4 h-4" /> PDF Siswa Ini</button>
              <button onClick={() => downloadFile(`/admin/periode/${periodeId}/export-pdf/sosiometri`, "Sosiometri_Semua.pdf")} className="bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2"><FileDown className="w-4 h-4" /> PDF Semua</button>
              <button onClick={() => downloadFile(`/admin/periode/${periodeId}/export/sosiometri`, "Sosiometri.xlsx")} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2" data-testid="export-sosio-xlsx">
                <FileSpreadsheet className="w-4 h-4" /> Unduh Excel
              </button>
            </div>
          )}
        </div>

        {!data && periodeId && <div className="text-slate-500 text-sm">Memuat...</div>}
      </div>

      {/* Preview */}
      {data && (
        <>
          {/* Single preview (shown on screen + on print "single") */}
          <div className="print-single overflow-x-auto print:overflow-visible">
            {current && (
              <PrintSosio
                settings={data.settings}
                siswa={current.siswa}
                raters={current.raters}
                sosio_values={current.sosiometri}
                unsur_penilaian={data.unsur_penilaian}
                periode={data.periode}
              />
            )}
          </div>

          {/* All-print (hidden normally, shown only when printing-all) */}
          <div className="print-all-wrap" style={{ display: "none" }}>
            {data.hasil.map((h, i) => (
              <div key={h.siswa.id} style={{ pageBreakAfter: i < data.hasil.length - 1 ? "always" : "auto" }}>
                <PrintSosio
                  settings={data.settings}
                  siswa={h.siswa}
                  raters={h.raters}
                  sosio_values={h.sosiometri}
                  unsur_penilaian={data.unsur_penilaian}
                  periode={data.periode}
                />
              </div>
            ))}
          </div>
        </>
      )}

      <style>{`
        @media print {
          .printing-all .print-single { display: none !important; }
          .printing-all .print-all-wrap { display: block !important; }
        }
        body.printing-all .print-all-wrap { display: block !important; }
        body.printing-all .print-single { display: none !important; }
      `}</style>
    </div>
  );
}
